import { useMemo, useState, useEffect } from "react";
import CurrencyInput from "./CurrencyInput";

/**
 * What-if Playground — standalone box (live, never saved).
 *
 * Editable inputs (seeded from the backend prediction/property):
 *   - Monthly predicted rent (AI)
 *   - Taxes (per month)
 *   - HOA (per month)
 *   - Sqft
 *   - Asking price
 *
 * Calculated outputs:
 *   - Val. Cap %  (default 4% — drives Val @ Cap)
 *   - NOI
 *   - Val @ Cap
 *   - Ask PPSF
 *   - CC PPSF
 *   - Cap at Ask
 *
 * NOTE: the calculated formulas below are provisional standard real-estate
 * formulas. Karel to supply the exact calculations — swap them in `calc` only.
 */
export default function WhatIfPlayground({
  agentRent = 0,
  monthlyTax = 0,
  monthlyHOA = 0,
  sqft = 0,
  askingPrice = 0,
}) {
  const [pgRent, setPgRent] = useState(Math.round(agentRent || 0));
  const [pgTax, setPgTax] = useState(Math.round(monthlyTax || 0));
  const [pgHOA, setPgHOA] = useState(Math.round(monthlyHOA || 0));
  const [pgSqft, setPgSqft] = useState(Math.round(sqft || 0));
  const [pgPrice, setPgPrice] = useState(Math.round(askingPrice || 0));
  const [pgValCap, setPgValCap] = useState(4); // Val. Cap % — default 4%

  // Re-seed each field once real data arrives (props can populate after first render).
  useEffect(() => { if (!pgRent && agentRent) setPgRent(Math.round(agentRent)); }, [agentRent]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!pgTax && monthlyTax) setPgTax(Math.round(monthlyTax)); }, [monthlyTax]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!pgHOA && monthlyHOA) setPgHOA(Math.round(monthlyHOA)); }, [monthlyHOA]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!pgSqft && sqft) setPgSqft(Math.round(sqft)); }, [sqft]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!pgPrice && askingPrice) setPgPrice(Math.round(askingPrice)); }, [askingPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetToCurrent = () => {
    setPgRent(Math.round(agentRent || 0));
    setPgTax(Math.round(monthlyTax || 0));
    setPgHOA(Math.round(monthlyHOA || 0));
    setPgSqft(Math.round(sqft || 0));
    setPgPrice(Math.round(askingPrice || 0));
    setPgValCap(4);
  };

  // ── Calculated values ──────────────────────────────────────────────────
  // PROVISIONAL formulas — Karel to confirm/replace.
  const calc = useMemo(() => {
    const rent = pgRent || 0;
    const tax = pgTax || 0;
    const hoa = pgHOA || 0;
    const sf = pgSqft || 0;
    const price = pgPrice || 0;
    const valCap = pgValCap || 0;

    // NOI (annual) = (rent - taxes - HOA) * 12
    const noi = (rent - tax - hoa) * 12;
    // Val @ Cap = NOI / (Val. Cap % / 100)
    const valAtCap = valCap > 0 ? noi / (valCap / 100) : 0;
    // Ask PPSF = asking price / sqft
    const askPPSF = sf > 0 ? price / sf : 0;
    // CC PPSF = common charges (taxes + HOA) / sqft
    const ccPPSF = sf > 0 ? (tax + hoa) / sf : 0;
    // Cap at Ask = NOI / asking price
    const capAtAsk = price > 0 ? (noi / price) * 100 : 0;

    return { noi, valAtCap, askPPSF, ccPPSF, capAtAsk };
  }, [pgRent, pgTax, pgHOA, pgSqft, pgPrice, pgValCap]);

  // ── Styles ─────────────────────────────────────────────────────────────
  const containerStyle = {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
    border: "2px solid #000",
    marginBottom: "20px",
  };
  const titleStyle = {
    fontSize: "18px",
    fontWeight: "700",
    marginBottom: "16px",
    color: "#333",
  };
  const inputStyle = {
    display: "block",
    width: "100%",
    marginTop: "4px",
    padding: "7px 9px",
    border: "1px solid #cfe2ff",
    borderRadius: "4px",
    fontSize: "14px",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: "13px", color: "#333", fontWeight: 600 };
  const outRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 12px",
    borderBottom: "1px solid #eef2f7",
  };
  const outLabelStyle = { fontSize: "13px", color: "#555", fontWeight: 600 };
  const outValStyle = { fontSize: "15px", color: "#0d47a1", fontWeight: 700 };

  const fmtCurrency = (v) => {
    const n = v === 0 ? 0 : Math.round(v);
    return n < 0 ? `-$${Math.abs(n).toLocaleString()}` : `$${n.toLocaleString()}`;
  };
  const fmtPPSF = (v) => (v === 0 ? "$0" : `$${Math.round(v).toLocaleString()}`);
  const fmtPercent = (v) => `${(v === 0 ? 0 : v).toFixed(2)}%`;

  return (
    <div style={containerStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={titleStyle}>
          What-if Playground{" "}
          <span style={{ fontWeight: 400, color: "#666", fontSize: "13px" }}>(live, not saved)</span>
        </div>
        <button
          onClick={resetToCurrent}
          style={{ fontSize: "12px", padding: "5px 12px", cursor: "pointer", background: "#fff", border: "1px solid #cfe2ff", borderRadius: "4px", color: "#0d47a1" }}
        >
          Reset to current
        </button>
      </div>

      <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
        {/* Editable inputs */}
        <div style={{ flex: "1 1 280px", minWidth: "260px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <label style={labelStyle}>Monthly predicted rent (AI)
              <CurrencyInput value={pgRent} onChange={(v) => setPgRent(v === "" ? 0 : v)} style={inputStyle} />
            </label>
            <label style={labelStyle}>Asking price
              <CurrencyInput value={pgPrice} onChange={(v) => setPgPrice(v === "" ? 0 : v)} style={inputStyle} />
            </label>
            <label style={labelStyle}>Taxes (per month)
              <CurrencyInput value={pgTax} onChange={(v) => setPgTax(v === "" ? 0 : v)} style={inputStyle} />
            </label>
            <label style={labelStyle}>HOA (per month)
              <CurrencyInput value={pgHOA} onChange={(v) => setPgHOA(v === "" ? 0 : v)} style={inputStyle} />
            </label>
            <label style={labelStyle}>Sqft
              <input type="number" value={pgSqft} onChange={(e) => setPgSqft(e.target.value === "" ? 0 : Number(e.target.value))} style={inputStyle} />
            </label>
            <label style={labelStyle}>Val. Cap %
              <input type="number" step="0.25" value={pgValCap} onChange={(e) => setPgValCap(e.target.value === "" ? 0 : Number(e.target.value))} style={inputStyle} />
            </label>
          </div>
        </div>

        {/* Calculated outputs */}
        <div style={{ flex: "1 1 280px", minWidth: "260px", border: "1px solid #eef2f7", borderRadius: "8px", overflow: "hidden" }}>
          <div style={{ ...outRowStyle, background: "#f0f7ff" }}>
            <span style={outLabelStyle}>Val. Cap %</span>
            <span style={outValStyle}>{fmtPercent(pgValCap)}</span>
          </div>
          <div style={outRowStyle}>
            <span style={outLabelStyle}>NOI</span>
            <span style={outValStyle}>{fmtCurrency(calc.noi)}</span>
          </div>
          <div style={outRowStyle}>
            <span style={outLabelStyle}>Val @ Cap</span>
            <span style={outValStyle}>{fmtCurrency(calc.valAtCap)}</span>
          </div>
          <div style={outRowStyle}>
            <span style={outLabelStyle}>Ask PPSF</span>
            <span style={outValStyle}>{fmtPPSF(calc.askPPSF)}</span>
          </div>
          <div style={outRowStyle}>
            <span style={outLabelStyle}>CC PPSF</span>
            <span style={outValStyle}>{fmtPPSF(calc.ccPPSF)}</span>
          </div>
          <div style={{ ...outRowStyle, borderBottom: "none" }}>
            <span style={outLabelStyle}>Cap at Ask</span>
            <span style={outValStyle}>{fmtPercent(calc.capAtAsk)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
